/*
 * AddToRequirementDialog - "add this candidate to one or many requirements" (and the
 * reverse: "add one or many candidates to this requirement"), then ask:
 * "Create a dossier now / Stay here".
 *
 * Ruhshad, 7 Sep 2026: "ADDING THE CANDIDATE TO THE REQUIREMENT SHOULD BE A VERY EASY
 * PROCESS ... ALLOW THE CANDIDATE TO BE ADDED TO NOT JUST ONE BUT MULTIPLE REQUIREMENTS.
 * ONCE ADDED ASK THE RECRUITER WOULD THEY LIKE TO GO TO THE CREATE A DOSSIER PAGE OR
 * STAY ON THE SAME PAGE."
 *
 * Mounted from three places (all via claude-fixes/apply_add_to_requirement.py):
 *   CandidateDetailPage   Actions dropdown -> "Add to requirement"   mode="candidate"
 *   RequirementDetailPage "Add candidates" button                    mode="requirement"
 *   CandidatesPage        bulk bar -> "Add to requirement"           mode="candidate" (many ids)
 *
 * Backend: routes/requirement_links.py
 *   GET  /api/requirement-links/search?q=&status=open|closed|all&candidate_id=   requirement picker
 *   GET  /api/requirement-links/candidates/search?q=&requirement_id=              candidate picker
 *   POST /api/requirement-links/add {candidate_ids, requirement_ids, stage?}       idempotent multi-add
 *
 * "Create a dossier" = the Requirement page with that candidate's row scrolled into view
 * and its dossier section open:  /ats/requirements/<rid>?dossier_for=<requirement_candidate_id>
 * (the same apply script teaches RequirementDetailPage to honour ?dossier_for=). The
 * dossier buttons (HTML dossier / PDF dossier / redacted CV) already live on that row.
 *
 * Props
 *   open, onOpenChange           controlled, like every other Dialog in the app
 *   mode                         "candidate" (pick requirements) | "requirement" (pick candidates)
 *   candidateIds, candidateNames mode=candidate: the master candidates to add
 *   requirementId, requirementTitle
 *                                mode=requirement: the requirement to add to
 *   onAdded(result)              called after a successful POST (pages reload their lists)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Search, Loader2, Briefcase, Users, CheckCircle2, AlertCircle, FileText, X, Building2, Link2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function authHeaders() {
  const token = localStorage.getItem('ats_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiGet(path, params) {
  const qs = new URLSearchParams(params || {});
  const res = await fetch(`${API_URL}/api/requirement-links${path}?${qs.toString()}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const STATUS_LABEL = {
  open: 'Open', in_progress: 'In progress', on_hold: 'On hold', closed: 'Closed', filled: 'Filled',
};
const PRIORITY_CLASS = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-sky-100 text-sky-700 border-sky-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

/** Small debounce hook - the pickers search as you type without hammering the API. */
function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function stageSummary(stageCounts) {
  const order = ['uploaded', 'screened', 'shortlisted', 'lined_up', 'interviewed', 'offered', 'joined', 'rejected'];
  const parts = order.filter((k) => stageCounts && stageCounts[k]).map((k) => `${stageCounts[k]} ${k.replace('_', ' ')}`);
  return parts.slice(0, 4).join(' · ');
}

export default function AddToRequirementDialog({
  open, onOpenChange, mode = 'candidate',
  candidateIds = [], candidateNames = [],
  requirementId = null, requirementTitle = '',
  onAdded,
}) {
  const navigate = useNavigate();
  const isCandidateMode = mode !== 'requirement';

  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const [statusFilter, setStatusFilter] = useState('open'); // open | all (requirement picker only)
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // id -> row
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);   // POST result -> follow-up screen
  const inputRef = useRef(null);

  const reset = useCallback(() => {
    setQ(''); setRows([]); setSelected({}); setResult(null); setSubmitting(false); setStatusFilter('open');
  }, []);

  useEffect(() => { if (!open) reset(); }, [open, reset]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current && inputRef.current.focus(), 50); }, [open]);

  // ----- load picker rows -----------------------------------------------------------------
  useEffect(() => {
    if (!open || result) return undefined;
    let cancelled = false;
    setLoading(true);
    const load = isCandidateMode
      ? apiGet('/search', {
          q: dq, status: statusFilter, limit: 40,
          ...(candidateIds.length === 1 ? { candidate_id: candidateIds[0] } : {}),
        }).then((d) => d.requirements || [])
      : apiGet('/candidates/search', { q: dq, requirement_id: requirementId || '', limit: 40 })
          .then((d) => d.candidates || []);
    load
      .then((list) => { if (!cancelled) setRows(list); })
      .catch(() => { if (!cancelled) { setRows([]); toast.error('Could not load the list - is the requirement-links module installed?'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dq, statusFilter, result, isCandidateMode, requirementId, candidateIds.join(',')]);

  const selectedIds = useMemo(() => Object.keys(selected), [selected]);
  const toggle = (row) => {
    if (row.already_linked) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id]; else next[row.id] = row;
      return next;
    });
  };

  const subjectLabel = isCandidateMode
    ? (candidateIds.length === 1
        ? (candidateNames[0] || 'this candidate')
        : `${candidateIds.length} candidates`)
    : (requirementTitle || 'this requirement');

  // ----- submit ---------------------------------------------------------------------------
  const submit = async () => {
    if (selectedIds.length === 0) { toast.error(isCandidateMode ? 'Pick at least one requirement' : 'Pick at least one candidate'); return; }
    setSubmitting(true);
    try {
      const body = isCandidateMode
        ? { candidate_ids: candidateIds, requirement_ids: selectedIds }
        : { candidate_ids: selectedIds, requirement_ids: [requirementId] };
      const res = await fetch(`${API_URL}/api/requirement-links/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult({ ...data, picked: selectedIds.map((id) => selected[id]) });
      if (data.added > 0) toast.success(`Added to ${data.added} ${isCandidateMode ? (data.added === 1 ? 'requirement' : 'requirements') : (data.added === 1 ? 'candidate' : 'candidates')}`);
      else if (data.exists > 0 && data.failed === 0) toast.info('Already on the selected requirement(s) - nothing to add');
      if (onAdded) onAdded(data);
    } catch (e) {
      toast.error(e.message || 'Could not add');
    } finally {
      setSubmitting(false);
    }
  };

  // ----- follow-up: dossier now / stay --------------------------------------------------
  // one button per requirement (candidate mode) or one for the requirement (requirement mode)
  const dossierTargets = useMemo(() => {
    if (!result) return [];
    const ok = (result.results || []).filter((r) => r.requirement_candidate_id);
    const byReq = {};
    ok.forEach((r) => {
      if (!byReq[r.requirement_id]) byReq[r.requirement_id] = { requirement_id: r.requirement_id, title: r.requirement_title, client: r.client_name, rc_ids: [], names: [] };
      byReq[r.requirement_id].rc_ids.push(r.requirement_candidate_id);
      if (r.candidate_name) byReq[r.requirement_id].names.push(r.candidate_name);
    });
    return Object.values(byReq);
  }, [result]);

  const goDossier = (t) => {
    onOpenChange(false);
    navigate(`/ats/requirements/${t.requirement_id}?dossier_for=${encodeURIComponent(t.rc_ids.join(','))}`);
  };

  const closeAndStay = () => onOpenChange(false);

  // ----- render -------------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="add-to-requirement-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-emerald-600" />
                {isCandidateMode ? <>Add <span className="text-emerald-700">{subjectLabel}</span> to requirements</> : <>Add candidates to <span className="text-emerald-700">{subjectLabel}</span></>}
              </DialogTitle>
              <DialogDescription>
                {isCandidateMode
                  ? 'Tick one or more requirements. Already-linked ones are greyed out; nothing is ever added twice.'
                  : 'Search the candidate database and tick everyone who should be on this requirement.'}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={isCandidateMode ? 'Search role, client or location...' : 'Search name, email, phone, code, title...'}
                  className="pl-9"
                  data-testid="add-to-requirement-search"
                />
              </div>
              {isCandidateMode && (
                <div className="flex rounded-md border border-slate-200 overflow-hidden text-sm">
                  {['open', 'all'].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatusFilter(s)}
                      className={`px-3 py-1.5 ${statusFilter === s ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                      data-testid={`add-to-requirement-filter-${s}`}
                    >
                      {s === 'open' ? 'Open only' : 'All'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border rounded-md max-h-[50vh] overflow-y-auto divide-y" data-testid="add-to-requirement-list">
              {loading && rows.length === 0 ? (
                <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : rows.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500">
                  {isCandidateMode ? 'No requirements match.' : 'No candidates match.'}
                  {isCandidateMode && statusFilter === 'open' && <> Try <button type="button" className="underline" onClick={() => setStatusFilter('all')}>All</button>.</>}
                </div>
              ) : rows.map((r) => {
                const checked = !!selected[r.id];
                const disabled = !!r.already_linked;
                return (
                  <label
                    key={r.id}
                    className={`flex items-start gap-3 px-3 py-2.5 ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'cursor-pointer hover:bg-emerald-50/40'} ${checked ? 'bg-emerald-50' : ''}`}
                    data-testid={`add-to-requirement-row-${r.id}`}
                  >
                    <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => toggle(r)} className="mt-1" />
                    <div className="flex-1 min-w-0">
                      {isCandidateMode ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-slate-800 truncate">{r.position_title || 'Untitled'}</span>
                            <span className="text-xs text-slate-500">× {r.headcount || 1}</span>
                            {r.priority && <Badge variant="outline" className={`text-[10px] ${PRIORITY_CLASS[r.priority] || ''}`}>{r.priority}</Badge>}
                            <Badge variant="outline" className={`text-[10px] ${r.is_open ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}`}>{STATUS_LABEL[r.status] || r.status || '—'}</Badge>
                            {disabled && <Badge className="text-[10px] bg-slate-200 text-slate-700">Already on it</Badge>}
                          </div>
                          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 mt-0.5">
                            <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{r.client_name || 'No client'}</span>
                            {r.location && <span>· {r.location}</span>}
                            <span className="inline-flex items-center gap-1">· <Users className="w-3 h-3" />{r.candidate_count || 0} in pipeline</span>
                            {stageSummary(r.stage_counts) && <span className="text-slate-400">({stageSummary(r.stage_counts)})</span>}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-slate-800 truncate">{r.name || 'Unnamed'}</span>
                            {r.candidate_code && <span className="font-mono text-[11px] text-slate-500">{r.candidate_code}</span>}
                            {disabled && <Badge className="text-[10px] bg-slate-200 text-slate-700">Already on it</Badge>}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">
                            {[r.current_title, r.current_company, r.location, r.experience_years != null ? `${r.experience_years} yrs` : null].filter(Boolean).join(' · ') || '—'}
                          </div>
                          <div className="text-xs text-slate-400 truncate">{[r.email, r.phone].filter(Boolean).join(' · ')}</div>
                        </>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>

            <DialogFooter className="flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex-1 flex flex-wrap items-center gap-1 text-xs text-slate-500">
                {selectedIds.length === 0 ? 'Nothing selected yet' : (
                  <>
                    <span className="font-medium text-slate-700">{selectedIds.length} selected:</span>
                    {selectedIds.slice(0, 4).map((id) => (
                      <span key={id} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5">
                        {(isCandidateMode ? selected[id].position_title : selected[id].name) || id.slice(0, 6)}
                        <button type="button" onClick={() => toggle(selected[id])} aria-label="Remove"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                    {selectedIds.length > 4 && <span>+{selectedIds.length - 4} more</span>}
                  </>
                )}
              </div>
              <Button variant="outline" onClick={closeAndStay} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || selectedIds.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-to-requirement-confirm">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Briefcase className="w-4 h-4 mr-2" />}
                {isCandidateMode
                  ? `Add to ${selectedIds.length || ''} requirement${selectedIds.length === 1 ? '' : 's'}`
                  : `Add ${selectedIds.length || ''} candidate${selectedIds.length === 1 ? '' : 's'}`}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                {result.added > 0
                  ? `Added to ${result.added} ${isCandidateMode ? (result.added === 1 ? 'requirement' : 'requirements') : (result.added === 1 ? 'candidate' : 'candidates')}`
                  : 'Nothing new to add'}
              </DialogTitle>
              <DialogDescription>
                {result.exists > 0 && <>{result.exists} already linked (skipped). </>}
                {result.failed > 0 && <span className="text-red-600">{result.failed} failed. </span>}
                Would you like to create a dossier now, or stay on this page?
              </DialogDescription>
            </DialogHeader>

            <div className="border rounded-md divide-y max-h-[40vh] overflow-y-auto" data-testid="add-to-requirement-result">
              {(result.results || []).map((r) => (
                <div key={`${r.candidate_id}-${r.requirement_id}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                  {r.status === 'added' ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    : r.status === 'exists' ? <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
                    : <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />}
                  <div className="flex-1 min-w-0 truncate">
                    <span className="font-medium">{r.candidate_name || r.candidate_id}</span>
                    <span className="text-slate-400"> → </span>
                    <span>{r.requirement_title || r.requirement_id}</span>
                    {r.client_name && <span className="text-slate-500"> · {r.client_name}</span>}
                  </div>
                  <span className={`text-xs ${r.status === 'added' ? 'text-emerald-700' : r.status === 'exists' ? 'text-slate-500' : 'text-red-600'}`}>
                    {r.status === 'added' ? `added (${r.stage})` : r.status === 'exists' ? 'already on it' : (r.detail || 'error')}
                  </span>
                </div>
              ))}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={closeAndStay} data-testid="add-to-requirement-stay">Stay here</Button>
              {dossierTargets.length === 1 ? (
                <Button onClick={() => goDossier(dossierTargets[0])} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-to-requirement-dossier">
                  <FileText className="w-4 h-4 mr-2" />
                  Create a dossier now
                </Button>
              ) : dossierTargets.map((t) => (
                <Button key={t.requirement_id} onClick={() => goDossier(t)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`add-to-requirement-dossier-${t.requirement_id}`}>
                  <FileText className="w-4 h-4 mr-2" />
                  Dossier: {t.title || 'requirement'}{t.client ? ` (${t.client})` : ''}
                </Button>
              ))}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
